import React, { createContext, useContext, useState, ReactNode } from 'react';

/**
 * Demo Mode Context - Provides fully mocked data for all dashboard tabs
 * No API calls, no costs - everything is simulated
 */

// Single demo project - Gwinnett County
const DEMO_PROJECT = {
  id: 'demo-lawrenceville-luxury',
  project_name: 'Lawrenceville Luxury Estate',
  address: '1250 Scenic Hwy, Lawrenceville, GA 30045',
  status: 'active',
  progress: 45,
  square_footage: 4850,
  total_budget: 850000,
  start_date: '2025-09-15',
  estimated_completion: '2026-03-15',
  notes: 'Custom luxury home with modern finishes. Currently in framing phase.',
  phases: {
    foundation: true,
    framing: true,
    mep: false,
    interior: false,
    finish: false
  },
  created_at: '2025-09-01T10:00:00Z',
  updated_at: new Date().toISOString()
};

// Mock floor plans
const MOCK_FLOOR_PLANS = [
  {
    id: 'floor-1',
    name: 'First Floor',
    level: 1,
    square_footage: 2650,
    rooms: ['Kitchen', 'Living Room', 'Dining Room', 'Office', 'Powder Room'],
    status: 'approved',
    thumbnail: '/demo/floor1-thumb.jpg'
  },
  {
    id: 'floor-2',
    name: 'Second Floor',
    level: 2,
    square_footage: 2200,
    rooms: ['Master Bedroom', 'Bedroom 2', 'Bedroom 3', 'Bedroom 4', 'Loft'],
    status: 'approved',
    thumbnail: '/demo/floor2-thumb.jpg'
  }
];

// Mock site plan data
const MOCK_SITE_DATA = {
  lot_size: '0.75 acres',
  zoning: 'R-1 (Single-Family Residential)',
  setbacks: {
    front: '25 feet',
    rear: '20 feet',
    side: '15 feet each'
  },
  utilities: {
    water: 'Municipal',
    sewer: 'Municipal',
    electric: 'Georgia Power',
    gas: 'Natural Gas Available'
  },
  easements: ['15ft drainage easement - rear property line'],
  permits: {
    building: 'BP-2025-09847',
    status: 'Approved',
    expiration: '2026-09-15'
  }
};

// Mock vendors
const MOCK_VENDORS = [
  {
    id: 'vendor-1',
    name: 'Gwinnett Foundation Pros',
    trade: 'Foundation',
    rating: 4.8,
    reviews: 127,
    phone: '(770) 555-0100',
    email: 'contact@gwinnettfoundation.com',
    verified: true,
    insurance: true
  },
  {
    id: 'vendor-2',
    name: 'Suwanee Framing Solutions',
    trade: 'Framing',
    rating: 4.9,
    reviews: 89,
    phone: '(678) 555-0101',
    email: 'bids@suwaneeframing.com',
    verified: true,
    insurance: true
  },
  {
    id: 'vendor-3',
    name: 'Duluth MEP Specialists',
    trade: 'MEP',
    rating: 4.7,
    reviews: 156,
    phone: '(770) 555-0102',
    email: 'info@duluthmep.com',
    verified: true,
    insurance: true
  }
];

// Mock estimates/bids
const MOCK_ESTIMATES = [
  {
    id: 'est-1',
    category: 'Foundation',
    vendor: 'Gwinnett Foundation Pros',
    amount: 125000,
    status: 'accepted',
    line_items: [
      { description: 'Excavation & Grading', amount: 18000 },
      { description: 'Footings & Foundation Walls', amount: 65000 },
      { description: 'Waterproofing & Drainage', amount: 22000 },
      { description: 'Materials & Equipment', amount: 20000 }
    ]
  },
  {
    id: 'est-2',
    category: 'Framing',
    vendor: 'Suwanee Framing Solutions',
    amount: 85000,
    status: 'accepted',
    line_items: [
      { description: 'Lumber & Materials', amount: 45000 },
      { description: 'Labor', amount: 32000 },
      { description: 'Structural Engineering', amount: 8000 }
    ]
  },
  {
    id: 'est-3',
    category: 'MEP (Mechanical, Electrical, Plumbing)',
    vendor: 'Duluth MEP Specialists',
    amount: 145000,
    status: 'pending',
    line_items: [
      { description: 'HVAC System', amount: 55000 },
      { description: 'Electrical Wiring & Panels', amount: 45000 },
      { description: 'Plumbing Rough-In', amount: 35000 },
      { description: 'Smart Home Integration', amount: 10000 }
    ]
  },
  {
    id: 'est-4',
    category: 'Roofing',
    vendor: 'TBD - Pending Bids',
    amount: 45000,
    status: 'pending',
    line_items: []
  },
  {
    id: 'est-5',
    category: 'Interior Finishes',
    vendor: 'TBD - Pending Bids',
    amount: 125000,
    status: 'pending',
    line_items: []
  }
];

// Mock communications
const MOCK_COMMUNICATIONS = [
  {
    id: 'comm-1',
    from: 'Suwanee Framing Solutions',
    subject: 'Framing Progress Update',
    date: '2025-10-18',
    preview: 'We have completed 75% of the framing work. Expecting to finish by...',
    unread: true
  },
  {
    id: 'comm-2',
    from: 'Gwinnett Foundation Pros',
    subject: 'Foundation Inspection Passed',
    date: '2025-10-15',
    preview: 'Good news! The foundation inspection passed with no issues...',
    unread: false
  },
  {
    id: 'comm-3',
    from: 'Duluth MEP Specialists',
    subject: 'MEP Bid Proposal',
    date: '2025-10-12',
    preview: 'Attached is our detailed bid for the HVAC, electrical, and plumbing work...',
    unread: false
  }
];

// Mock phase timeline
const MOCK_PHASES = [
  {
    id: 'phase-1',
    name: 'Foundation',
    status: 'completed',
    startDate: '2025-09-15',
    endDate: '2025-10-05',
    progress: 100,
    tasks: [
      { name: 'Excavation', completed: true },
      { name: 'Footings', completed: true },
      { name: 'Foundation walls', completed: true },
      { name: 'Waterproofing', completed: true }
    ]
  },
  {
    id: 'phase-2',
    name: 'Framing',
    status: 'in-progress',
    startDate: '2025-10-06',
    endDate: '2025-11-10',
    progress: 75,
    tasks: [
      { name: 'Floor joists', completed: true },
      { name: 'Wall framing', completed: true },
      { name: 'Roof trusses', completed: false },
      { name: 'Sheathing', completed: false }
    ]
  },
  {
    id: 'phase-3',
    name: 'MEP (Mechanical, Electrical, Plumbing)',
    status: 'pending',
    startDate: '2025-11-11',
    endDate: '2025-12-20',
    progress: 0,
    tasks: [
      { name: 'HVAC rough-in', completed: false },
      { name: 'Electrical rough-in', completed: false },
      { name: 'Plumbing rough-in', completed: false }
    ]
  },
  {
    id: 'phase-4',
    name: 'Interior Finishes',
    status: 'pending',
    startDate: '2025-12-21',
    endDate: '2026-02-15',
    progress: 0,
    tasks: []
  },
  {
    id: 'phase-5',
    name: 'Final',
    status: 'pending',
    startDate: '2026-02-16',
    endDate: '2026-03-15',
    progress: 0,
    tasks: []
  }
];

interface DemoModeContextType {
  isDemoMode: boolean;
  project: typeof DEMO_PROJECT;
  floorPlans: typeof MOCK_FLOOR_PLANS;
  siteData: typeof MOCK_SITE_DATA;
  vendors: typeof MOCK_VENDORS;
  estimates: typeof MOCK_ESTIMATES;
  communications: typeof MOCK_COMMUNICATIONS;
  phases: typeof MOCK_PHASES;
  updateProject: (updates: Partial<typeof DEMO_PROJECT>) => void;
  togglePhase: (phaseName: string) => void;
}

const DemoModeContext = createContext<DemoModeContextType | undefined>(undefined);

export function DemoModeProvider({ children }: { children: ReactNode }) {
  const [project, setProject] = useState(DEMO_PROJECT);
  const [phases, setPhases] = useState(MOCK_PHASES);

  const updateProject = (updates: Partial<typeof DEMO_PROJECT>) => {
    setProject(prev => ({
      ...prev,
      ...updates,
      updated_at: new Date().toISOString()
    }));
  };

  const togglePhase = (phaseName: string) => {
    setProject(prev => ({
      ...prev,
      phases: {
        ...prev.phases,
        [phaseName]: !prev.phases[phaseName as keyof typeof prev.phases]
      },
      updated_at: new Date().toISOString()
    }));
  };

  return (
    <DemoModeContext.Provider
      value={{
        isDemoMode: true,
        project,
        floorPlans: MOCK_FLOOR_PLANS,
        siteData: MOCK_SITE_DATA,
        vendors: MOCK_VENDORS,
        estimates: MOCK_ESTIMATES,
        communications: MOCK_COMMUNICATIONS,
        phases,
        updateProject,
        togglePhase
      }}
    >
      {children}
    </DemoModeContext.Provider>
  );
}

export function useDemoMode() {
  const context = useContext(DemoModeContext);
  return context || {
    isDemoMode: false,
    project: null,
    floorPlans: [],
    siteData: null,
    vendors: [],
    estimates: [],
    communications: [],
    phases: [],
    updateProject: () => {},
    togglePhase: () => {}
  };
}
